import { createFileRoute } from '@tanstack/react-router';
import { isIsoDate, today } from '@cis/shared';
import { taskDetailQuery } from '../../api/tasks.ts';
import { parseFreezeParam } from '../../features/freeze/use-freeze-param.ts';
import { isTaskId, TaskPage } from '../../features/tasks/task-page.tsx';

export interface TaskPageSearch {
  readonly asOf?: string | undefined;
  readonly freeze?: number | undefined;
}

/**
 * `?asOf=` must be a calendar date up to today in program time; anything else is dropped, so a
 * future day opens the live page instead of the API's 400 (review D-R26). `?freeze=` is a freeze
 * id (T32); a frozen view wins over a past day, since both are read-only.
 */
export function validateTaskPageSearch(search: Record<string, unknown>): TaskPageSearch {
  const freeze = parseFreezeParam(search['freeze']) ?? undefined;
  const raw = search['asOf'];
  const ok = freeze === undefined && typeof raw === 'string' && isIsoDate(raw) && raw <= today();
  return { asOf: ok ? raw : undefined, freeze };
}

// Task page (T21). The tasks layout route above already checks task.read.
export const Route = createFileRoute('/_authed/tasks/$taskId')({
  validateSearch: validateTaskPageSearch,
  loaderDeps: ({ search }) => ({ asOf: search.asOf, freeze: search.freeze }),
  loader: ({ context, params, deps }) => {
    if (isTaskId(params.taskId) && deps.freeze === undefined) {
      void context.queryClient.prefetchQuery(taskDetailQuery(params.taskId, deps.asOf));
    }
  },
  component: TaskRoute,
});

function TaskRoute(): React.JSX.Element {
  const { taskId } = Route.useParams();
  const { asOf } = Route.useSearch();
  const navigate = Route.useNavigate();
  return (
    <TaskPage
      key={taskId}
      taskId={taskId}
      asOf={asOf}
      onAsOf={(next) => void navigate({ search: { asOf: next } })}
    />
  );
}
